import { useState } from 'react';
import { Gift, Mail, MessageSquare, CreditCard } from 'lucide-react';
import { summaryApi } from 'common';
import { useTheme } from "../../components/Theme/ThemeProvider";
import { authFetch } from '../../helpers/authFetch';

export function PurchaseGiftCard() {
  const [formData, setFormData] = useState({
    amount: '',
    recipientEmail: '',
    recipientName: '',
    message: '',
    design: 'default',
    paymentMethod: 'Flutterwave' // NEW: Default payment method
  });
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [giftCardDetails, setGiftCardDetails] = useState(null);
  const { colors } = useTheme();

  const predefinedAmounts = [5000, 10000, 25000, 50000, 100000];
  const designs = [
    { id: 'default', name: 'Classic', color: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)` },
    { id: 'celebration', name: 'Celebration', color: 'linear-gradient(135deg, #EC4899 0%, #F43F5E 100%)' },
    { id: 'tech', name: 'Tech', color: 'linear-gradient(135deg, #1F2937 0%, #111827 100%)' },
    { id: 'nature', name: 'Nature', color: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }
  ];

  // NEW: Payment methods
  const paymentMethods = [
    { id: 'Flutterwave', name: 'Flutterwave', icon: '💳' },
    { id: 'Stripe', name: 'Stripe', icon: '💳' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAmountSelect = (amount) => {
    setFormData(prev => ({
      ...prev,
      amount: amount.toString()
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (!formData.amount || formData.amount < 50) {
        throw new Error('Amount must be at least ₦50');
      }
      if (!formData.recipientEmail) {
        throw new Error('Recipient email is required');
      }

      // Step 1: Create gift card
      const response = await authFetch(summaryApi.purchaseGiftCard.url, {
        method: 'POST',
        
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: parseFloat(formData.amount),
          recipientEmail: formData.recipientEmail,
          recipientName: formData.recipientName,
          message: formData.message,
          design: formData.design,
          paymentMethod: formData.paymentMethod
        })
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Failed to create gift card');
      }

      if (data.data.paymentRequired) {
        // Step 2: Process payment
        const paymentResponse = await authFetch(summaryApi.processGiftCardPayment.url, {
          method: 'POST',
          
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            giftCardId: data.data.giftCardId,
            paymentMethod: formData.paymentMethod
          })
        });

        const paymentData = await paymentResponse.json();

        if (!paymentData.success) {
          throw new Error(paymentData.message || 'Failed to process payment');
        }

        // Redirect to payment gateway
        if (paymentData.session_url) {
  // Stripe returns session_url directly
  window.location.href = paymentData.session_url;
} else if (paymentData.data?.link) {
  // Flutterwave returns link in data object
  window.location.href = paymentData.data.link;
} else {
  throw new Error('Payment link not found');
}
      } else {
        // Payment completed (e.g., COD)
        setSuccess(true);
        setGiftCardDetails(data.data);
        setFormData({
          amount: '',
          recipientEmail: '',
          recipientName: '',
          message: '',
          design: 'default',
          paymentMethod: 'Flutterwave'
        });
      }
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  if (success && giftCardDetails) {
    return (
      <div className="min-h-screen py-12 px-4" style={{ backgroundColor: colors.bgPrimary }}>
        <div className="max-w-2xl mx-auto">
          <div className="rounded-lg shadow-lg p-8 text-center" style={{ backgroundColor: colors.bgSecondary }}>
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" 
              style={{ backgroundColor: `${colors.success}20` }}>
              <Gift className="w-10 h-10" style={{ color: colors.success }} />
            </div>
            <h2 className="text-3xl font-bold mb-4" style={{ color: colors.text }}>
              Gift Card Sent Successfully!
            </h2>
            <p className="mb-6" style={{ color: colors.textSecondary }}>
              Your gift card has been sent to {giftCardDetails.recipientEmail}
            </p>
            
            <div className="rounded-lg p-6 mb-6" style={{ backgroundColor: colors.bgTertiary }}>
              <p className="text-sm mb-2" style={{ color: colors.textSecondary }}>Gift Card Code</p>
              <p className="text-2xl font-mono font-bold tracking-wider mb-4" style={{ color: colors.primary }}>
                {giftCardDetails.code}
              </p>
              <p className="text-sm" style={{ color: colors.textSecondary }}>
                Amount: ₦{giftCardDetails.amount?.toLocaleString()}
              </p>
              <p className="text-xs mt-2" style={{ color: colors.textSecondary }}>
                Valid until: {new Date(giftCardDetails.expiryDate).toLocaleDateString()}
              </p>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => setSuccess(false)}
                className="w-full py-3 rounded-lg text-white transition"
                style={{ backgroundColor: colors.primary }}
              >
                Send Another Gift Card
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4" style={{ backgroundColor: colors.bgPrimary }}>
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-2" style={{ color: colors.text }}>Send a Gift Card</h1>
          <p style={{ color: colors.textSecondary }}>Give the perfect gift for any occasion</p>
        </div>

        <div className="rounded-lg shadow-lg p-8" style={{ backgroundColor: colors.bgSecondary }}>
          {/* Amount Selection */}
          <div className="mb-8">
            <label className="block text-sm font-medium mb-3" style={{ color: colors.text }}>
              Select Amount (NGN)
            </label>
            <div className="grid grid-cols-3 gap-3 mb-4">
              {predefinedAmounts.map(amount => (
                <button
                  key={amount}
                  type="button"
                  onClick={() => handleAmountSelect(amount)}
                  className="py-3 px-4 rounded-lg border-2 font-medium transition"
                  style={{
                    borderColor: formData.amount === amount.toString() ? colors.primary : colors.border,
                    backgroundColor: formData.amount === amount.toString() ? `${colors.primary}10` : colors.bgPrimary,
                    color: formData.amount === amount.toString() ? colors.primary : colors.text
                  }}
                >
                  ₦{amount.toLocaleString()}
                </button>
              ))}
            </div>
            <div className="relative">
              <span className="absolute left-3 top-3" style={{ color: colors.textSecondary }}>₦</span>
              <input
                type="number"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                placeholder="Custom amount"
                className="w-full pl-8 pr-4 py-3 rounded-lg focus:outline-none focus:ring-2"
                style={{
                  backgroundColor: colors.bgPrimary,
                  color: colors.text,
                  border: `1px solid ${colors.border}`
                }}
                min="50"
                max="1000000"
              />
            </div>
          </div>

          {/* Payment Method Selection - NEW */}
          <div className="mb-8">
            <label className="block text-sm font-medium mb-3" style={{ color: colors.text }}>
              Choose Payment Method
            </label>
            <div className="grid grid-cols-2 gap-4">
              {paymentMethods.map(method => (
                <button
                  key={method.id}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, paymentMethod: method.id }))}
                  className="py-4 px-6 rounded-lg border-2 font-medium transition flex items-center justify-center gap-3"
                  style={{
                    borderColor: formData.paymentMethod === method.id ? colors.primary : colors.border,
                    backgroundColor: formData.paymentMethod === method.id ? `${colors.primary}10` : colors.bgPrimary,
                    color: formData.paymentMethod === method.id ? colors.primary : colors.text
                  }}
                >
                  <span className="text-2xl">{method.icon}</span>
                  <span>{method.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Design Selection */}
          <div className="mb-8">
            <label className="block text-sm font-medium mb-3" style={{ color: colors.text }}>
              Choose Design
            </label>
            <div className="grid grid-cols-4 gap-4">
              {designs.map(design => (
                <button
                  key={design.id}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, design: design.id }))}
                  className="relative h-24 rounded-lg transition"
                  style={{
                    background: design.color,
                    boxShadow: formData.design === design.id ? `0 0 0 4px ${colors.primary}` : 'none'
                  }}
                >
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Gift className="w-8 h-8 text-white opacity-50" />
                  </div>
                  <span className="absolute bottom-2 left-0 right-0 text-white text-xs font-medium">
                    {design.name}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Recipient Email */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>
              <Mail className="w-4 h-4 inline mr-2" />
              Recipient Email *
            </label>
            <input
              type="email"
              name="recipientEmail"
              value={formData.recipientEmail}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2"
              style={{
                backgroundColor: colors.bgPrimary,
                color: colors.text,
                border: `1px solid ${colors.border}`
              }}
              placeholder="recipient@example.com"
            />
          </div>

          {/* Recipient Name */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>
              Recipient Name (Optional)
            </label>
            <input
              type="text"
              name="recipientName"
              value={formData.recipientName}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2"
              style={{
                backgroundColor: colors.bgPrimary,
                color: colors.text,
                border: `1px solid ${colors.border}`
              }}
              placeholder="John Doe"
            />
          </div>

          {/* Message */}
          <div className="mb-8">
            <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>
              <MessageSquare className="w-4 h-4 inline mr-2" />
              Personal Message (Optional)
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={4}
              maxLength={500}
              className="w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 resize-none"
              style={{
                backgroundColor: colors.bgPrimary,
                color: colors.text,
                border: `1px solid ${colors.border}`
              }}
              placeholder="Add a personal message..."
            />
            <p className="text-xs mt-1" style={{ color: colors.textSecondary }}>
              {formData.message.length}/500 characters
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 border rounded-lg" style={{
              backgroundColor: `${colors.error}10`,
              borderColor: colors.error
            }}>
              <p className="text-sm" style={{ color: colors.error }}>{error}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full py-4 rounded-lg font-medium text-white transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            style={{ backgroundColor: colors.primary }}
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Processing...
              </>
            ) : (
              <>
                <CreditCard className="w-5 h-5 mr-2" />
                Purchase Gift Card
              </>
            )}
          </button>

          {/* Note */}
          <div className="mt-6 p-4 rounded-lg" style={{ backgroundColor: `${colors.primary}10` }}>
            <p className="text-sm" style={{ color: colors.text }}>
              <strong>Note:</strong> The gift card will be emailed to the recipient immediately after payment confirmation. 
              Valid for 3 years from the date of issue.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}